import type { Triangulation, Vec2 } from '../../geo/types';
import {
  addTriangle,
  apexOf,
  aroundOf,
  cornersOf,
  createTriangleSheet,
  liveIndices,
  neighboursOf,
  onOutline,
  retireTriangle,
  trianglesOn,
  type Corners,
  type TriangleSheet as Sheet,
} from './triangleSheet';

// A triangle smaller or lower than this covers no pixel. Refined, it splits into a fan of slivers just
// as empty: the clipped far land's millimetre steps once grew into hundreds of them.
export const MIN_TRIANGLE_AREA_M2 = 1e-6;
export const MIN_TRIANGLE_HEIGHT_M = 1e-3;
// A triangle of the smallest area and the lowest height has a longest side this long, so a degenerate
// triangle with no side this short is a cap, lying flat along its longest side.
const NEEDLE_M = (2 * MIN_TRIANGLE_AREA_M2) / MIN_TRIANGLE_HEIGHT_M;
// Refined, the tip of a spike a few centimetres wide becomes a stack of slivers lying along each other,
// none of which can flip; one goes by merging its shortest side when that is shorter than this, a few
// single-precision steps 45 km out.
const STUB_M = 0.01;
// A band a millimetre or two high inside the sheet, refined, leaves such a stack with its corners a
// few centimetres apart along the band. Where both ends of the side lie inside the sheet the merge
// moves no outline, and an inner vertex this much, well under a painter-view pixel, bends no edge.
const INNER_STUB_M = 0.05;
// A merge removes a vertex, a drop a triangle and a flip a degenerate triangle, so mending ends; the
// cap only guards against a mistake in that reasoning.
const MENDS_PER_DEFECT = 16;
type Side = readonly [number, number];
type Made = readonly number[] | null;

// The mesh stores positions in single precision, in which neighbouring values 45 km out lie 4 mm
// apart, so a triangle is judged at the corners it will have.
function storedAt(positions: Float64Array, v: number): Vec2 {
  return [Math.fround(positions[v * 2]!), Math.fround(positions[v * 2 + 1]!)];
}

function distance(a: Vec2, b: Vec2): number {
  return Math.hypot(b[0] - a[0], b[1] - a[1]);
}

function squaredLength(ax: number, ay: number, bx: number, by: number): number {
  return (bx - ax) * (bx - ax) + (by - ay) * (by - ay);
}

// Facing down counts too, its area being below the smallest. It runs on every refined triangle, so it
// reads the corners in place.
function isDegenerate(positions: Float64Array, a: number, b: number, c: number): boolean {
  const ax = Math.fround(positions[a * 2]!);
  const ay = Math.fround(positions[a * 2 + 1]!);
  const bx = Math.fround(positions[b * 2]!);
  const by = Math.fround(positions[b * 2 + 1]!);
  const cx = Math.fround(positions[c * 2]!);
  const cy = Math.fround(positions[c * 2 + 1]!);
  const doubled = (bx - ax) * (cy - ay) - (cx - ax) * (by - ay);
  if (doubled < 2 * MIN_TRIANGLE_AREA_M2) return true;
  const longest = Math.max(
    squaredLength(ax, ay, bx, by),
    squaredLength(bx, by, cx, cy),
    squaredLength(cx, cy, ax, ay)
  );
  return doubled * doubled < MIN_TRIANGLE_HEIGHT_M * MIN_TRIANGLE_HEIGHT_M * longest;
}

function facesUp(positions: Float64Array, [a, b, c]: Corners): boolean {
  const [p, q, r] = [storedAt(positions, a), storedAt(positions, b), storedAt(positions, c)];
  return (q[0] - p[0]) * (r[1] - p[1]) - (r[0] - p[0]) * (q[1] - p[1]) > 0;
}

function isDegenerateAt(sheet: Sheet, t: number): boolean {
  return sheet.alive[t] === true && isDegenerate(sheet.positions, ...cornersOf(sheet, t));
}

// Merging the ends of a side keeps a sheet only where the vertices beside both ends are the apexes of
// the triangles on the side; any other shared vertex would fold two edges into one.
function mergesCleanly(sheet: Sheet, keep: number, drop: number): boolean {
  const apexes = trianglesOn(sheet, keep, drop).map((t) => apexOf(sheet, t, keep, drop));
  const besideKeep = neighboursOf(sheet, keep);
  const shared = [...neighboursOf(sheet, drop)].filter((w) => w !== keep && besideKeep.has(w));
  return shared.length === apexes.length && shared.every((w) => apexes.includes(w));
}

function mergeInto(sheet: Sheet, keep: number, drop: number): Made {
  if (!mergesCleanly(sheet, keep, drop)) return null;
  const around = aroundOf(sheet, drop);
  const moved = around
    .map((t) => cornersOf(sheet, t))
    .filter((corners) => !corners.includes(keep))
    .map((corners) => corners.map((v) => (v === drop ? keep : v)) as unknown as Corners);
  if (!moved.every((corners) => facesUp(sheet.positions, corners))) return null;
  for (const t of around) retireTriangle(sheet, t);
  return moved.map((corners) => addTriangle(sheet, corners));
}

// An end on the outline stays where it is, so the outline moves only where both ends lie on it.
function mergeSide(sheet: Sheet, [u, v]: Side): Made {
  const [uOut, vOut] = [onOutline(sheet, u), onOutline(sheet, v)];
  if (uOut !== vOut) return uOut ? mergeInto(sheet, u, v) : mergeInto(sheet, v, u);
  return mergeInto(sheet, u, v) ?? mergeInto(sheet, v, u);
}

function flip(sheet: Sheet, t: number, [a, b, c]: Corners, across: number): Made {
  const d = apexOf(sheet, across, a, b);
  const flipped: Corners[] = [
    [a, d, c],
    [d, b, c],
  ];
  const clear = trianglesOn(sheet, c, d).length === 0;
  if (!clear || flipped.some((corners) => isDegenerate(sheet.positions, ...corners))) return null;
  retireTriangle(sheet, t);
  retireTriangle(sheet, across);
  return flipped.map((corners) => addTriangle(sheet, corners));
}

// A cap's apex lies on its longest side, between the ends. Inside the sheet the other diagonal of the
// cap and its neighbour replaces that side, where that leaves two sound triangles; four corners in a
// line would only flip back and forth. A cap standing on the outline goes, and so does one whose
// apex is an outline vertex bent less than a millimetre off the line of its neighbours, where the
// outline turns away and leaves no room to flip; the outline moves less than a millimetre either way.
function flipOrDrop(sheet: Sheet, t: number, corners: Corners): Made {
  const [a, b, c] = corners;
  const across = trianglesOn(sheet, a, b).filter((other) => other !== t);
  const flipped = across.length === 1 ? flip(sheet, t, corners, across[0]!) : null;
  if (flipped) return flipped;
  const ear = trianglesOn(sheet, b, c).length === 1 && trianglesOn(sheet, c, a).length === 1;
  if (across.length !== 0 && !ear) return null;
  retireTriangle(sheet, t);
  return [];
}

function stubOf(sheet: Sheet, [u, v]: Side): number {
  return onOutline(sheet, u) || onOutline(sheet, v) ? STUB_M : INNER_STUB_M;
}

function mend(sheet: Sheet, t: number): Made {
  const corners = cornersOf(sheet, t);
  const at = corners.map((v) => storedAt(sheet.positions, v));
  const sides = [0, 1, 2].map((k) => distance(at[k]!, at[(k + 1) % 3]!));
  const s = sides.indexOf(Math.min(...sides));
  const l = sides.indexOf(Math.max(...sides));
  const shortest: Side = [corners[s]!, corners[(s + 1) % 3]!];
  const cap: Corners = [corners[l]!, corners[(l + 1) % 3]!, corners[(l + 2) % 3]!];
  if (sides[s]! < NEEDLE_M) return mergeSide(sheet, shortest) ?? flipOrDrop(sheet, t, cap);
  const stub = sides[s]! < stubOf(sheet, shortest);
  return flipOrDrop(sheet, t, cap) ?? (stub ? mergeSide(sheet, shortest) : null);
}

// Triangles a mend makes are judged next, so a fan of slivers goes in one sweep; a triangle that could
// not be mended is tried again once its neighbourhood has changed.
function mendAll(sheet: Sheet, queue: number[]): void {
  let stuck: number[] = [];
  for (let budget = queue.length * MENDS_PER_DEFECT; queue.length > 0 && budget > 0; budget--) {
    const t = queue.pop()!;
    if (!isDegenerateAt(sheet, t)) continue;
    const made = mend(sheet, t);
    if (made === null) {
      stuck.push(t);
      continue;
    }
    queue.push(...stuck, ...made);
    stuck = [];
  }
}

function degenerateTriangles({ positions, indices }: Triangulation): number[] {
  const found: number[] = [];
  for (let i = 0; i < indices.length; i += 3) {
    if (isDegenerate(positions, indices[i]!, indices[i + 1]!, indices[i + 2]!)) found.push(i / 3);
  }
  return found;
}

/**
 * The triangulation without triangles under MIN_TRIANGLE_AREA_M2 or lower than MIN_TRIANGLE_HEIGHT_M in
 * single precision. Every edge inside stays shared by two triangles and no triangle is added; vertices
 * keep their indices, and one merged away is left unused.
 */
export function withoutDegenerates(input: Triangulation): Triangulation {
  const degenerate = degenerateTriangles(input);
  if (degenerate.length === 0) return input;
  const sheet = createTriangleSheet(input);
  mendAll(sheet, degenerate);
  return { positions: input.positions, indices: liveIndices(sheet) };
}
